import Router from "next/router";
import _ from "lodash";
import { create } from "zustand";
import {
  getTrainingOrganizations,
  getEventsByTrainingOrgId,
  searchByInstructorName,
  getObjectives,
  searchTrainings,
  fetchTrainingAgeGroups,
  loadBrightspaceOrgs,
  loadCoxCampusOrgs,
  loadOHTrainOrgs,
  loadInsightOrgs,
  createTraining,
  updateTraining,
  loadInserviceTypes,
  getEventById,
  getConfiguration,
  getTrainingFindings,
  deleteTraining,
  createOutline,
  getTrainingFiles,
  searchScheduledEvents,
  getScheduledEventsByEventId,
  deleteScheduledEvent,
} from "@/services/eventManagement";
import { FORMAT_TYPES } from "@/pages/eventManagement/helpers/formats";
import dayjs from "dayjs";
import { http } from "@/services/http";
import { useAppStore } from "@/stores/appStore";
import { hasTrainingOrgAccess } from "@/utils/orgPermissions";

export const useEventManagementStore = create((set) => ({
  trainingOrganizations: [],
  selectedTrainingOrganization: null,
  permissionsByTrainingOrg: null,
  trainingOrgsLoading: false,
  initialTrainingOrgLoad: false,
  eventsLoading: false,
  searchByInstructorLoading: false,
  objectivesLoading: false,
  totalEvents: 0,
  trainingEvents: [],
  instructorSearchResults: [],
  objectives: [],
  trainingsLoading: false,
  trainings: [],
  approvedAgeGroups: [],
  approvedAgeGroupsLoading: false,
  trainingSaving: false,
  brightspaceOrgs: [],
  insightOrgs: [],
  coxcampusOrgs: [],
  ohtrainOrgs: [],
  isBrightspace: false,
  isInsight: false,
  isCoxCampus: false,
  isOHTrain: false,
  brightspaceAndInsightOrgsLoading: false,
  inserviceTypes: [],
  inserviceTypesLoading: false,
  initialEvent: null,
  loadingInitialEvent: false,
  error: "",
  configurationLoading: false,
  configuration: null,
  findings: null,
  findingsLoading: false,
  setError: (error) => set({ error }),
  fetchTrainingOrganizations: async () => {
    const { trainingOrgsLoading } = useEventManagementStore.getState();

    if (trainingOrgsLoading) {
      return;
    }
    set({ trainingOrgsLoading: true });
    try {
      const data = await getTrainingOrganizations();
      const sortedData = data.sort((a, b) => {
        if (a.name < b.name) return -1;
        if (a.name > b.name) return 1;
        return 0;
      });
      const mappedData = sortedData.map((org) => {
        // We only want to show SDA ID if it's less than 13
        if (org.sda_id && org.sda_id !== "0" && Number(org.sda_id) < 13) {
          org.name = `${org.name} (SDA ${org.sda_id})`;
        }
        return org;
      });
      set({ trainingOrganizations: mappedData });
      return mappedData;
    } catch (error) {
      console.log("Error fetching training orgs: ", error);
      set((state) => ({
        ...state,
        error:
          "Unable to fetch Training org events at this time. Please try again or contact support.",
      }));
    } finally {
      set((state) => ({
        ...state,
        trainingOrgsLoading: false,
      }));
    }
  },
  checkPermissionsByTrainingOrg: (permissionToCheck) => {
    const { permissionsByTrainingOrg } = useEventManagementStore.getState();
    if (!permissionsByTrainingOrg?.org_permissions) {
      return false;
    }
    return (
      permissionsByTrainingOrg.org_permissions.indexOf(permissionToCheck) > -1
    );
  },
  // Only fetches and returns this org's permissions -- setSelectedTrainingOrganization
  // stores them together with selectedTrainingOrganization.
  fetchPermissionsByTrainingOrg: async (trainingOrgId) => {
    const { selectedTrainingOrganization } = useEventManagementStore.getState();
    const orgId = trainingOrgId ?? selectedTrainingOrganization?.id;
    if (!orgId) {
      return null;
    }

    return http
      .get(`/api/v2/organization/${orgId}/user/me`)
      .then((response) => {
        if (response.status < 200 || response.status >= 300) {
          throw new Error("The response was not OK");
        }
        return response.data;
      })
      .catch((error) => {
        console.error("There was a problem loading the event:", error);
        return null;
      });
  },
  fetchEventsByTrainingOrg: async (
    trainingOrgId,
    pageNumber,
    pageSize,
    sortBy,
    isDesc,
    filterModel,
    showArchived
  ) => {
    const {
      eventsLoading,
      configuration,
      fetchConfiguration,
      selectedTrainingOrganization,
    } = useEventManagementStore.getState();
    if (!configuration) {
      await fetchConfiguration();
    }
    if (eventsLoading) return;
    set((state) => ({
      ...state,
      eventsLoading: true,
    }));
    try {
      const { data, total } = await getEventsByTrainingOrgId(
        trainingOrgId,
        pageNumber,
        pageSize,
        sortBy,
        isDesc,
        filterModel,
        showArchived
      );

      const formatApprovalStatus = (event) => {
        const status = event.highest_level;
        if (
          status === "N/A" &&
          event.event_type === "training" &&
          event.template_status === "approved"
        ) {
          return "Non-OA";
        }
        return status;
      };
      const formatDeliveryMethods = (methods) => {
        if (!methods) return "";

        // Turn result into an array if it isn't already
        if (!Array.isArray(methods)) {
          if (!methods?.includes(",")) {
            // If a single word without commas, wrap it in an array
            methods = [methods];
          } else {
            // Else it's a string with commas, split it into an array
            methods = methods.split(",");
          }
        }

        // Convert it to the abbreviated format
        let abbreviated = methods.map((method) => {
          const match = configuration?.training_formats_array.find((format) => {
            return format.value === method;
          });

          if (match) {
            return abbreviatedTrainingFormats[match.value] || match.label;
          } else {
            return "";
          }
        });
        return abbreviated.join(", ");
      };
      set((state) => ({
        ...state,
        trainingEvents: data.map((event, idx) => {
          return {
            ...event,
            id: idx,
            shared: event.owner_id !== selectedTrainingOrganization.id,
            delivery_methods: formatDeliveryMethods(event.delivery_methods),
            highest_level: formatApprovalStatus(event),
            template_status:
              event.template_status === "awaiting_info"
                ? "Awaiting Info"
                : event.template_status,
          };
        }),
        totalEvents: total,
      }));
    } catch (error) {
      set((state) => ({
        ...state,
        error:
          "Unable to fetch Training org events at this time. Please try again or contact support.",
      }));
    } finally {
      set((state) => ({
        ...state,
        eventsLoading: false,
      }));
    }
  },
  setSelectedTrainingOrganization: async (
    trainingOrg,
    trainingOrgIdFromUrl
  ) => {
    const { fetchLMSOrgs, fetchPermissionsByTrainingOrg } =
      useEventManagementStore.getState();

    if (!trainingOrg) {
      set({ selectedTrainingOrganization: null, permissionsByTrainingOrg: null });
      return;
    }

    // Load this org's permissions before routing. ERC supervisors without training
    // org access may only administer user permissions, so every other Event
    // Management page 403s for them and must not be used as the landing page.
    const orgMe = await fetchPermissionsByTrainingOrg(trainingOrg.id);
    // Store the org and its permissions together before routing, so a page
    // that renders right after the redirect never sees one without the other.
    set({ selectedTrainingOrganization: trainingOrg, permissionsByTrainingOrg: orgMe });
    const { user } = useAppStore.getState();
    const usersPageOnly = !!user && !hasTrainingOrgAccess(user, orgMe);

    if (usersPageOnly) {
      if (!Router.pathname.endsWith("/users") || trainingOrgIdFromUrl !== trainingOrg.id) {
        Router.push(`/eventManagement/${trainingOrg.id}/users`);
      }
    } else if (Router.pathname.includes("/eventManagement")) {
      const paths = Router.pathname.split("/");
      const currentPage = paths[3];
      if (trainingOrgIdFromUrl !== trainingOrg.id) {
        // Substitute the correct organization ID and redirect
        Router.push(`/eventManagement/${trainingOrg.id}/${currentPage}`);
      }
    } else {
      Router.push(`/eventManagement/${trainingOrg.id}/events`);
    }
    const {
      brightspace = [],
      insight = [],
      coxcampus = [],
      ohtrain = [],
    } = (await fetchLMSOrgs()) ?? {};
    // Map Data as needed
    set({
      initialTrainingOrgLoad: true,
      isBrightspace:
        brightspace?.findIndex((OIN) => trainingOrg.id === OIN) !== -1,
      isInsight: insight?.findIndex((OIN) => trainingOrg.id === OIN) !== -1,
      isCoxCampus: coxcampus?.findIndex((OIN) => trainingOrg?.id === OIN) !== -1,
      isOHTrain: ohtrain?.findIndex((OIN) => trainingOrg?.id === OIN) !== -1,
    });
  },
  searchByInstructor: async (instructorName) => {
    const { searchByInstructorLoading } = useEventManagementStore.getState();
    if (searchByInstructorLoading) return;
    set((state) => ({
      ...state,
      searchByInstructorLoading: true,
    }));
    try {
      const { users } = await searchByInstructorName(instructorName);
      const mappedInstructors = users.map((user) => {
        return {
          id: user.id,
          authorApprovals: user.approvals || undefined,
          label: `${user.full_name} OPIN: ${user.opin}`,
        };
      });
      set((state) => ({
        ...state,
        instructorSearchResults: mappedInstructors,
      }));
      return mappedInstructors;
    } catch (error) {
      set((state) => ({
        ...state,
        error:
          "Unable to fetch Instructors at this time. Please try again or contact support.",
      }));
    } finally {
      set((state) => ({
        ...state,
        searchByInstructorLoading: false,
      }));
    }
  },
  fetchObjectives: async () => {
    const { objectivesLoading } = useEventManagementStore.getState();
    if (objectivesLoading) return;
    set((state) => ({
      ...state,
      objectivesLoading: true,
    }));
    try {
      const data = await getObjectives();
      set((state) => ({
        ...state,
        objectives: data,
      }));
      return data;
    } catch (error) {
      set((state) => ({
        ...state,
        error:
          "Unable to fetch objectives at this time. Please try again or contact support.",
      }));
    } finally {
      set((state) => ({
        ...state,
        objectivesLoading: false,
      }));
    }
  },
  setTrainings: (trainings) => {
    set((state) => ({
      ...state,
      trainings,
    }));
  },
  searchTrainingsByOrg: async (
    searchString,
    searchParameters,
    filterSessions = [],
    isConference = false
  ) => {
    const { trainingsLoading } = useEventManagementStore.getState();
    if (trainingsLoading) return;
    set((state) => ({
      ...state,
      trainingsLoading: true,
    }));
    try {
      let data = await searchTrainings(searchString, searchParameters);
      if (!data) return;
      // For conferences, we want to filter out any trainings that have ASYNC as the only delivery method
      if (isConference) {
        data = data.filter((training) => {
          return !(
            training.delivery_methods?.length === 1 &&
            training.delivery_methods.includes(FORMAT_TYPES.ONLINE_ASYNCH)
          );
        });
      }
      let trainings = data.map((training) => ({
        label: training.name,
        value: training.id,
      }));
      // In some cases, we need to filter out any duplicate trainings if they already exist in a session
      trainings = trainings.filter((training) => {
        if (filterSessions.length === 0) return true;
        return !filterSessions.some(
          (session) => session.template_id === training.value
        );
      });

      set((state) => ({
        ...state,
        trainings,
      }));
      return data;
    } catch (error) {
      set((state) => ({
        ...state,
        error:
          "Unable to fetch trainings at this time. Please try again or contact support.",
      }));
    } finally {
      set((state) => ({
        ...state,
        trainingsLoading: false,
      }));
    }
  },
  fetchApprovedAgeGroups: async (authorId) => {
    try {
      let data = await fetchTrainingAgeGroups(authorId);
      if (!data) return;
      set((state) => ({
        ...state,
        approvedAgeGroups: data.map((ageGroup) => ({
          value: ageGroup.id,
          label: ageGroup.age_group,
        })),
      }));
      return data;
    } catch (error) {
      set((state) => ({
        ...state,
        error:
          "Unable to fetch trainings at this time. Please try again or contact support.",
      }));
    }
  },
  saveTraining: async (payload, legacy) => {
    const { selectedTrainingOrganization, trainingSaving } =
      useEventManagementStore.getState();
    if (trainingSaving) return;
    try {
      if (payload.id) {
        const data = await updateTraining(payload, legacy);
        return data;
      } else {
        const data = await createTraining(
          selectedTrainingOrganization.id,
          payload,
          legacy
        );
        if (data.messages?.errors) {
          set((state) => ({
            ...state,
            error: data.messages.errors.title,
          }));
          return;
        }
        return {
          ...data,
          id: data.id || data.applicationId,
        };
      }
    } catch (error) {
      set((state) => ({
        ...state,
        error:
          "Unable to save training at this time. Please try again or contact support.",
      }));
    } finally {
      set((state) => ({
        ...state,
        trainingSaving: false,
      }));
    }
  },
  saveOutline: async (contentPlanId, payload) => {
    const { selectedTrainingOrganization } = useEventManagementStore.getState();
    try {
      const data = await createOutline(
        selectedTrainingOrganization.id,
        contentPlanId,
        payload
      );
      return data;
    } catch (error) {
      set((state) => ({
        ...state,
        error:
          "Unable to save outline at this time. Please try again or contact support.",
      }));
      throw error;
    }
  },
  fetchLMSOrgs: async () => {
    const {
      brightspaceAndInsightOrgsLoading,
      brightspaceOrgs,
      insightOrgs,
      coxcampusOrgs,
      ohtrainOrgs,
    } = useEventManagementStore.getState();
    // Cache this data and reuse it, it should not change often.
    if (
      brightspaceAndInsightOrgsLoading ||
      brightspaceOrgs?.length > 0 ||
      insightOrgs?.length > 0 ||
      coxcampusOrgs?.length > 0 ||
      ohtrainOrgs?.length > 0
    )
      return {
        brightspace: brightspaceOrgs,
        insight: insightOrgs,
        coxcampus: coxcampusOrgs,
        ohtrain: ohtrainOrgs,
      };
    set((state) => ({
      ...state,
      brightspaceAndInsightOrgsLoading: true,
    }));
    try {
      const [brightspaceOrgs, insightOrgs, coxcampusOrgs, ohtrainOrgs] = await Promise.all([
        loadBrightspaceOrgs(),
        loadInsightOrgs(),
        loadCoxCampusOrgs(),
        loadOHTrainOrgs(),
      ]);
      set((state) => ({
        ...state,
        brightspaceOrgs,
        insightOrgs,
        coxcampusOrgs,
        ohtrainOrgs,
      }));
      return {
        brightspace: brightspaceOrgs,
        insight: insightOrgs,
        coxcampus: coxcampusOrgs,
        ohtrain: ohtrainOrgs,
      };
    } catch (error) {
      set((state) => ({
        ...state,
        error:
          "Unable to fetch Brightspace and Insight Organizations at this time. Please try again or contact support.",
      }));
    } finally {
      set((state) => ({
        ...state,
        brightspaceAndInsightOrgsLoading: false,
      }));
    }
  },
  fetchInserviceTypes: async () => {
    const { inserviceTypesLoading } = useEventManagementStore.getState();
    if (inserviceTypesLoading) return;
    set((state) => ({
      ...state,
      inserviceTypesLoading: true,
    }));
    try {
      const data = await loadInserviceTypes();
      set((state) => ({
        ...state,
        inserviceTypes: data.map((inserviceType) => ({
          value: inserviceType.id,
          label: inserviceType.name,
        })),
      }));
      return data;
    } catch (error) {
      set((state) => ({
        ...state,
        error:
          "Unable to fetch Inservice Types at this time. Please try again or contact support.",
      }));
    } finally {
      set((state) => ({
        ...state,
        inserviceTypesLoading: false,
      }));
    }
  },
  fetchEventById: async (eventId, legacy) => {
    const {
      loadingInitialEvent,
      trainingOrganizations,
      fetchTrainingOrganizations,
    } = useEventManagementStore.getState();
    let { trainings } = useEventManagementStore.getState();
    if (loadingInitialEvent) return;
    set((state) => ({
      ...state,
      loadingInitialEvent: true,
    }));
    try {
      let orgs = trainingOrganizations;
      if (trainingOrganizations.length === 0) {
        orgs = await fetchTrainingOrganizations();
      }
      const [data, fileData] = await Promise.all([
        getEventById(eventId, legacy),
        getTrainingFiles(eventId),
      ]);

      const decodeHtmlEntities = (text) => {
        var textArea = document.createElement("textarea");
        textArea.innerHTML = text;
        return textArea.value;
      };

      const currentTrainingOrg = orgs.find((org) => org.id === data.owner_id);
      const author_vitae = fileData?.find((file) => file.type === "resume");
      let initialEvent = null;
      if (data && !Array.isArray(data) && data.length !== 0) {
        initialEvent = {
          ...data,
          org_name: currentTrainingOrg?.name || "",
          affirmation: data.is_draft !== "1",
          author_vitae,
        };
      }
      if (data.content_plan && data.content_plan.length > 0) {
        // Filter any blank strings from pd_topics array
        initialEvent.content_plan = data.content_plan.map((plan) => ({
          ...plan,
          objective: decodeHtmlEntities(plan?.objective),
          pd_topics:
            plan?.pd_topics && plan.pd_topics?.[0] !== ""
              ? plan.pd_topics[0]
              : "19",
        }));
      }
      if (data.ats && data.ats.length > 0) {
        initialEvent.ats = data.ats.map((at) => ({
          hours: at.hours,
          id: at.id,
          label: at.title,
        }));
      }
      set((state) => ({
        ...state,
        initialEvent,
        trainings,
      }));
      return initialEvent;
    } catch (error) {
      set((state) => ({
        ...state,
        error:
          "Unable to fetch Events at this time. Please try again or contact support.",
      }));
    } finally {
      set((state) => ({
        ...state,
        loadingInitialEvent: false,
      }));
    }
  },
  fetchingTrainingFormats: false,
  selectedSessionTraining: null,
  fetchTrainingFormatsByEventId: async (eventId) => {
    const { fetchingTrainingFormats } = useEventManagementStore.getState();
    if (fetchingTrainingFormats) return;
    set((state) => ({
      ...state,
      fetchingTrainingFormats: true,
    }));
    try {
      const event = await getEventById(eventId, false);
      set((state) => ({
        ...state,
        selectedSessionTraining: event,
      }));
      return event.delivery_methods;
    } catch (error) {
      set((state) => ({
        ...state,
        error:
          "Unable to fetch Training Formats at this time. Please try again or contact support.",
      }));
    } finally {
      set((state) => ({
        ...state,
        fetchingTrainingFormats: false,
      }));
    }
  },
  fetchConfiguration: async () => {
    const { configurationLoading } = useEventManagementStore.getState();
    if (configurationLoading) return;
    set((state) => ({
      ...state,
      configurationLoading: true,
    }));
    try {
      const data = await getConfiguration();
      data.training_formats_array = Object.entries(
        data.training_formats_array
      ).map(([key, value]) => {
        return {
          value: key,
          label: value,
          abbreviation: abbreviatedTrainingFormats[key],
        };
      });
      data.author_types = Object.entries(data.author_types).map(
        ([key, value]) => ({ value: Number(key), label: value })
      );
      data.author_types.sort((a, b) => a.label.localeCompare(b.label));

      set((state) => ({
        ...state,
        configuration: data,
      }));
      return data;
    } catch (error) {
      set((state) => ({
        ...state,
        error:
          "Unable to fetch Configuration at this time. Please try again or contact support.",
      }));
    } finally {
      set((state) => ({
        ...state,
        configurationLoading: false,
      }));
    }
  },
  fetchFindings: async () => {
    const { findingsLoading, findings } = useEventManagementStore.getState();
    if (findingsLoading) return;
    if (findings) return findings;
    set((state) => ({
      ...state,
      findingsLoading: true,
    }));
    try {
      const TEXT_FIELDS = ["title", "description", "references"];
      const findings = await getTrainingFindings();
      const textOnlyFindings = findings["*"].filter(
        (finding) => finding.appliesTo === "TEXT"
      )[0];
      const allFieldsFindings = findings["*"].filter(
        (finding) => finding.appliesTo === "ALL"
      )[0];

      TEXT_FIELDS.forEach((textField) => {
        if (!findings[textField]) {
          return;
        }
        const newFieldFindings = [
          ...findings[textField],
          _.cloneDeep(textOnlyFindings),
        ];
        findings[textField] = newFieldFindings;
      });
      Object.keys(findings).forEach((field) => {
        findings[field] = [...findings[field], _.cloneDeep(allFieldsFindings)];
      });
      // pd_topics is a special case where we only want to show the allFieldsFindings
      findings["pd_topics"] = [allFieldsFindings];
      set((state) => ({
        ...state,
        findings,
      }));
      return findings;
    } catch (error) {
      set((state) => ({
        ...state,
        error:
          "Unable to fetch Findings at this time. Please try again or contact support.",
      }));
    } finally {
      set((state) => ({
        ...state,
        findingsLoading: false,
      }));
    }
  },
  setInitialEvent: (event) => {
    set((state) => ({
      ...state,
      initialEvent: event,
    }));
  },
  deleteTrainingEvent: async (eventId) => {
    const { eventsLoading } = useEventManagementStore.getState();
    if (eventsLoading) return;
    try {
      await deleteTraining(eventId);
      return true;
    } catch (error) {
      set((state) => ({
        ...state,
        error:
          "Unable to delete Event at this time. Please try again or contact support.",
      }));
    }
  },
  scheduledEventsLoading: false,
  scheduledEvents: [],
  fetchScheduledEventsByTrainingOrg: async (
    pageNumber,
    pageSize,
    sortBy,
    isDesc,
    filterModel,
    archived = false
  ) => {
    const { selectedTrainingOrganization } = useEventManagementStore.getState();
    if (!selectedTrainingOrganization) return;
    const { scheduledEventsLoading } = useEventManagementStore.getState();
    if (scheduledEventsLoading) return;
    set((state) => ({
      ...state,
      scheduledEventsLoading: true,
    }));
    try {
      const { data, total } = await searchScheduledEvents(
        selectedTrainingOrganization.id,
        pageNumber,
        pageSize,
        sortBy,
        isDesc,
        filterModel,
        archived
      );

      data.map((event) => {
        event.start_date = event.start_date
          ? dayjs(event.start_date).format("MM-DD-YYYY")
          : "";
        event.registration_deadline_date = event.registration_deadline_date
          ? dayjs(event.registration_deadline_date).format("MM-DD-YYYY")
          : "";
        event.registration_opens = event.registration_opens
          ? dayjs(event.registration_opens).format("MM-DD-YYYY")
          : "";
        return event;
      });

      set((state) => ({
        ...state,
        scheduledEvents: data,
        totalScheduledEvents: total,
      }));
      return data;
    } catch (error) {
      set((state) => ({
        ...state,
        error:
          "Unable to fetch Scheduled Events at this time. Please try again or contact support.",
      }));
    } finally {
      set((state) => ({
        ...state,
        scheduledEventsLoading: false,
      }));
    }
  },
  totalScheduledEvents: 0,
  fetchScheduledEventsByEventId: async (eventId) => {
    const { scheduledEventsLoading } = useEventManagementStore.getState();
    if (scheduledEventsLoading) return;
    set((state) => ({
      ...state,
      scheduledEventsLoading: true,
    }));
    try {
      const data = await getScheduledEventsByEventId(eventId);
      if (!data) return;
      set((state) => ({
        ...state,
        scheduledEvents: data,
        totalScheduledEvents: data.length,
      }));
      return data;
    } catch (error) {
      set((state) => ({
        ...state,
        error:
          "Unable to fetch Scheduled Events at this time. Please try again or contact support.",
      }));
    } finally {
      set((state) => ({
        ...state,
        scheduledEventsLoading: false,
      }));
    }
  },
  deleteScheduledEventFromEvents: async (scheduledEventId) => {
    const { scheduledEventsLoading, scheduledEvents } =
      useEventManagementStore.getState();
    if (scheduledEventsLoading) return;
    set((state) => ({
      ...state,
      scheduledEventsLoading: true,
    }));
    try {
      await deleteScheduledEvent(scheduledEventId);
      set((state) => ({
        ...state,
        scheduledEvents: scheduledEvents.filter(
          (event) => event.id !== scheduledEventId
        ),
      }));
      return true;
    } catch (error) {
      set((state) => ({
        ...state,
        error:
          "Unable to delete Scheduled Event at this time. Please try again or contact support.",
      }));
    } finally {
      set((state) => ({
        ...state,
        scheduledEventsLoading: false,
      }));
    }
  },
}));

export const abbreviatedTrainingFormats = {
  1: "F2F",
  3: "SYNC",
  4: "ASYNC",
  5: "Hybrid",
  6: "Brightspace",
  7: "Insight",
  9: "Cox Campus",
  10: "OH Train",
};
